import React, { useState, useEffect } from "react";
import {
  FaHome,
  FaClock,
  FaTasks,
  FaExclamationTriangle,
  FaComments,
  FaFileAlt,
  FaMoneyBillWave,
  FaUser,
  FaCalendarAlt,
  FaAngleDoubleLeft,
  FaAngleDoubleRight,
} from "react-icons/fa";
import { db } from "../firebase";
import { collection, query, where, onSnapshot } from "firebase/firestore";
import { tolerantId } from "../utils/firestoreHelpers";
import { useBuilding } from "../context/BuildingContext";
import logo from "../assets/logo.png";

const SidebarEmployee = ({ selected, setSelected, collapsed = false, setCollapsed, userid }) => {
  const { selectedBuilding } = useBuilding();
  const [unreadChat, setUnreadChat] = useState(0);

  // Listen for unread chat messages for this employee
  useEffect(() => {
    if (!selectedBuilding?.buildingid || !userid) return;

    const empId = String(userid);

    const q = query(
      collection(db, "employee_chats"),
      where("buildingid", "in", tolerantId(selectedBuilding.buildingid)),
      where("employeeid", "==", empId)
    );

    const unsub = onSnapshot(
      q,
      (snap) => {
        let total = 0;
        snap.forEach((d) => {
          total += d.data().unreadCountEmployee || 0;
        });
        setUnreadChat(total);
      },
      (err) => {
        console.error("SidebarEmployee unread listener error:", err);
      }
    );

    return () => unsub();
  }, [selectedBuilding?.buildingid, userid]);

  const menuSections = [
    {
      title: "GENERAL",
      items: [
        { id: "homeemployee", icon: FaHome, label: "Inicio" },
        { id: "turnos", icon: FaCalendarAlt, label: "Turnos" },
      ],
    },
    {
      title: "OPERACIONES",
      items: [
        { id: "checkin", icon: FaClock, label: "Asistencia" },
        { id: "tareasemployee", icon: FaTasks, label: "Tareas" },
        { id: "incidentesemployee", icon: FaExclamationTriangle, label: "Incidencias" },
      ],
    },
    {
      title: "COMUNICACIÓN",
      items: [
        { id: "chatemployee", icon: FaComments, label: "Chat", badgeKey: "chat" },
        { id: "solicitudes", icon: FaFileAlt, label: "Licencias" },
      ],
    },
    {
      title: "PERSONAL",
      items: [
        { id: "recibos", icon: FaMoneyBillWave, label: "Liquidaciones" },
        { id: "perfil", icon: FaUser, label: "Mi Perfil" },
      ],
    },
  ];

  const getBadge = (item) => {
    if (item.badgeKey === "chat" && unreadChat > 0) return unreadChat;
    return 0;
  };

  const handleToggle = () => {
    if (setCollapsed) {
      setCollapsed(!collapsed);
    }
  };

  return (
    <aside
      style={{
        ...styles.sidebar,
        width: collapsed ? 64 : 220,
        minWidth: collapsed ? 64 : 220,
      }}
      role="navigation"
      aria-label="Menú principal empleado"
    >
      {/* Logo y Toggle */}
      <div style={{
        ...styles.header,
        justifyContent: collapsed ? "center" : "space-between",
        padding: collapsed ? "12px 6px" : "12px 10px",
      }}>
        {!collapsed && (
          <div
            style={styles.logoContainer}
            onClick={() => setSelected("homeemployee")}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === "Enter" && setSelected("homeemployee")}
            aria-label="Ir al inicio"
          >
            <img
              src={logo}
              alt="Habitia - Ir al inicio"
              style={{
                ...styles.logo,
                height: 48,
                filter: "brightness(0) invert(1)",
              }}
            />
            {process.env.REACT_APP_FIREBASE_PROJECT_ID === "habitia-dev-8b512" && (
              <span style={{
                display: "block",
                fontSize: 9,
                fontWeight: 700,
                letterSpacing: "0.12em",
                textTransform: "uppercase",
                color: "rgba(255,255,255,0.6)",
                marginTop: 2,
                lineHeight: 1,
              }}>Testing</span>
            )}
          </div>
        )}
        <button
          style={styles.toggleBtn}
          onClick={handleToggle}
          aria-label={collapsed ? "Expandir menú" : "Minimizar menú"}
        >
          {collapsed ? (
            <FaAngleDoubleRight size={14} color="#ffffff" />
          ) : (
            <FaAngleDoubleLeft size={14} color="#ffffff" />
          )}
        </button>
      </div>

      {/* Menú agrupado */}
      <nav style={styles.nav}>
        {menuSections.map((section, sectionIndex) => (
          <div key={section.title} style={styles.section}>
            {/* Título de sección */}
            {!collapsed && (
              <div style={styles.sectionTitle}>{section.title}</div>
            )}
            {collapsed && sectionIndex > 0 && (
              <div style={styles.sectionDivider}></div>
            )}

            {/* Items de la sección */}
            {section.items.map((item) => {
              const isActive = selected === item.id;
              const badge = getBadge(item);
              return (
                <div
                  key={item.id}
                  role="button"
                  tabIndex={0}
                  aria-current={isActive ? "page" : undefined}
                  style={{
                    ...styles.menuItem,
                    ...(isActive ? styles.menuItemActive : {}),
                    justifyContent: collapsed ? "center" : "flex-start",
                    padding: collapsed ? "8px" : "8px 10px",
                  }}
                  onClick={() => setSelected(item.id)}
                  onKeyDown={(e) => e.key === "Enter" && setSelected(item.id)}
                  onMouseEnter={(e) => {
                    if (!isActive) {
                      e.currentTarget.style.backgroundColor = "rgba(255, 255, 255, 0.1)";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isActive) {
                      e.currentTarget.style.backgroundColor = "transparent";
                    }
                  }}
                  title={collapsed ? item.label : ""}
                  aria-label={collapsed ? item.label : undefined}
                >
                  <item.icon
                    size={18}
                    color={isActive ? "#0080ff" : "rgba(255, 255, 255, 0.85)"}
                    style={{ flexShrink: 0 }}
                  />
                  {!collapsed && (
                    <span style={{
                      ...styles.menuLabel,
                      color: isActive ? "#0080ff" : "#ffffff",
                      fontWeight: 400,
                    }}>
                      {item.label}
                    </span>
                  )}
                  {badge > 0 && (
                    <span style={{
                      ...styles.badge,
                      ...(collapsed ? styles.badgeCollapsed : {}),
                    }}>
                      {badge > 99 ? "99+" : badge}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        ))}
      </nav>
    </aside>
  );
};

const styles = {
  sidebar: {
    position: "fixed",
    left: 0,
    top: 0,
    bottom: 0,
    backgroundColor: "#0080ff",
    display: "flex",
    flexDirection: "column",
    height: "100vh",
    minHeight: "100vh",
    flexShrink: 0,
    transition: "width 0.2s ease, min-width 0.2s ease",
    overflow: "hidden",
    fontFamily: "'FT Habit', -apple-system, BlinkMacSystemFont, sans-serif",
    zIndex: 100,
  },
  header: {
    display: "flex",
    alignItems: "center",
    borderBottom: "1px solid rgba(255, 255, 255, 0.2)",
  },
  logoContainer: {
    display: "flex",
    alignItems: "center",
    cursor: "pointer",
    flex: 1,
  },
  logo: {
    width: "auto",
    objectFit: "contain",
  },
  toggleBtn: {
    width: 32,
    height: 32,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "rgba(255, 255, 255, 0.15)",
    border: "none",
    borderRadius: 6,
    cursor: "pointer",
    transition: "all 0.15s ease",
    flexShrink: 0,
  },
  nav: {
    flex: 1,
    overflowY: "auto",
    padding: "8px 6px",
    display: "flex",
    flexDirection: "column",
    gap: 2,
    minHeight: 0,
  },
  section: {
    marginBottom: 4,
  },
  sectionTitle: {
    fontSize: 10,
    fontWeight: 400,
    color: "rgba(255, 255, 255, 0.65)",
    letterSpacing: "0.5px",
    padding: "4px 10px 2px",
    textTransform: "uppercase",
  },
  sectionDivider: {
    height: 1,
    backgroundColor: "rgba(255, 255, 255, 0.2)",
    margin: "4px 10px",
  },
  menuItem: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "8px 12px",
    borderRadius: 8,
    cursor: "pointer",
    transition: "all 0.15s ease",
    position: "relative",
  },
  menuItemActive: {
    backgroundColor: "#ffffff",
    boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
  },
  menuLabel: {
    fontSize: 14,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    flex: 1,
  },
  badge: {
    backgroundColor: "#ef4444",
    color: "#ffffff",
    fontSize: 10,
    fontWeight: 400,
    borderRadius: 10,
    padding: "1px 6px",
    minWidth: 18,
    textAlign: "center",
    lineHeight: "16px",
    flexShrink: 0,
  },
  badgeCollapsed: {
    position: "absolute",
    top: 2,
    right: 2,
    fontSize: 9,
    padding: "0px 4px",
    minWidth: 14,
    lineHeight: "14px",
  },
};

export default SidebarEmployee;
